<template>
  <div>
    <!-- Top navbar -->
    <nav class="navbar navbar-top navbar-expand-md bg-white shadow border-bottom" id="navbar-main">
      <div class="container-fluid">
        <router-link :to="{ name: 'completedSession' }" class="h4 mb-0 text-uppercase d-none d-lg-inline-block">
          Closed Session Details
        </router-link>
        <user-data></user-data>
      </div>
    </nav>

    <div class="container-fluid mt-0 pt-5 pt-md-8" v-if="siteDetails">

      <div class="session-summary-bar shadow-sm summary-box mb-3">
        <div class="stat-chip">
          <span class="stat-label">Site</span>
          <span class="stat-value">
            {{ siteDetails.site.name }}
            <b-link v-if="completedSessionLots.data && completedSessionLots.data.data && completedSessionLots.data.data.length"
              v-b-tooltip.hover="'Copy UUID'"
              @click="copyUuid(completedSessionLots.data.data[0].uuid)"
              class="text-info ml-1"><i class="fas fa-copy"></i></b-link>
          </span>
        </div>
        <div class="stat-chip">
          <span class="stat-label">Meter Type</span>
          <span class="stat-value">{{ siteDetails.service_type.service_name }}</span>
        </div>
        <div class="stat-chip">
          <span class="stat-label">Total Units Billed</span>
          <span class="stat-value">{{ sessionSummary.total_units || 0 | formatNumber }} <small class="text-muted font-weight-normal">{{ detailUnitLabel }}</small></span>
        </div>
        <div class="stat-chip">
          <span class="stat-label">Total Billed Value</span>
          <span class="stat-value">{{ sessionSummary.total_billed || 0 | currency }}</span>
        </div>
        <div class="stat-chip" v-if="hasUtilityBill">
          <span class="stat-label">Utility Bill<span v-if="isWaterDetailSession"> (CWA)</span></span>
          <span class="stat-value">{{ sessionSummary.utility_bill_total_units | formatNumber }} <small class="text-muted font-weight-normal">{{ billingUnit }}</small></span>
        </div>
        <div class="stat-chip" v-if="hasUtilityBill">
          <span class="stat-label">Average Rate</span>
          <span class="stat-value">{{ sessionSummary.average_rate | currency }} <small class="text-muted font-weight-normal">/ {{ billingUnit }}</small></span>
        </div>
        <div class="stat-chip" v-if="isWaterDetailSession">
          <span class="stat-label">Bowser Amount</span>
          <span class="stat-value">{{ sessionSummary.total_tanker_allocated || sessionSummary.total_bowser_allocated || 0 | currency }}</span>
        </div>
        <div class="stat-chip" v-if="isWaterDetailSession">
          <span class="stat-label">Allocation Basis <i class="fas fa-info-circle text-muted" v-b-tooltip.hover :title="allocationBasisMessage" style="cursor:help;"></i></span>
          <span class="stat-value">{{ allocationBasisLabel }}</span>
        </div>
        <div class="stat-chip" v-if="isWaterDetailSession">
          <span class="stat-label">Common Consumption</span>
          <span class="stat-value">{{ formatPercent(sessionSummary.estimated_common_ratio_used) }}</span>
        </div>
        <div class="stat-chip" v-if="isWaterDetailSession && mainMeterRow">
          <span class="stat-label">Main Meter</span>
          <span class="stat-value">{{ mainMeterRow.lot && mainMeterRow.lot.lot_no ? mainMeterRow.lot.lot_no : 'Main meter' }}</span>
        </div>
        <div class="stat-chip" v-if="isWaterDetailSession && mainMeterRow">
          <span class="stat-label">Main Supply</span>
          <span class="stat-value">{{ rowConsumption(mainMeterRow) | formatNumber }} <small class="text-muted font-weight-normal">{{ detailUnitLabel }}</small></span>
        </div>
        <div class="stat-chip" v-if="isWaterDetailSession && mainMeterRow">
          <span class="stat-label">Avg Common Ratio</span>
          <span class="stat-value">{{ formatPercent(sessionSummary.average_common_ratio_for_site) }}</span>
        </div>
        <div class="stat-chip">
          <span class="stat-label">Closed By</span>
          <span class="stat-value">{{ detailHeaderRow && detailHeaderRow.surveyor_closed ? detailHeaderRow.surveyor_closed.email : '-' }}</span>
        </div>
        <div class="stat-chip">
          <span class="stat-label">Closed Date</span>
          <span class="stat-value">{{ fmtDateTime(detailHeaderRow && detailHeaderRow.survey_end_date) }}</span>
        </div>
        <div class="stat-chip stat-chip--action" v-if="isWaterDetailSession">
          <b-button variant="outline-info" size="sm" @click="showCommonConsumptionModal">
            <i class="fas fa-chart-line mr-1"></i> Supply History
          </b-button>
        </div>
      </div>

      <div class="row mt-4">
        <div class="col-md-12 mb-5 mb-xl-0">
          <div class="card shadow">
            <div class="card-header border-0">
              <div class="row align-items-center">
                <div class="col d-flex align-items-center">
                  <router-link :to="{ name: 'completedSession' }" class="btn btn-primary mr-3">
                    <i class="fas fa-arrow-left mr-2"></i>
                    Back
                  </router-link>
                  <h3 class="mb-0">Lots Details</h3>
                  <b-button variant="link" size="sm" class="ml-2" v-b-tooltip.hover="'Refresh (emails, photos and Vilogi update in the background)'"
                    @click="loadLots(currentPage)"><i class="fas fa-sync-alt"></i></b-button>
                </div>
              </div>
            </div>

            <div class="table-responsive" v-if="pagination.total != 0">
              <b-table class="mt-3" striped hover :items="detailRows" :fields="lotFields">
                <template slot="ref" slot-scope="data">
                  <span v-if="data.item.replaces_id" v-b-tooltip.hover="'Replaces invoice #' + data.item.replaces_id">{{ data.item.id }}</span>
                  <span v-else>{{ data.item.id }}</span>
                </template>
                <template slot="lotNo" slot-scope="data">
                  {{ data.item.lot.lot_no }}
                  <b-badge v-if="data.item.is_voided" variant="danger" class="ml-1"
                    v-b-tooltip.hover="voidTooltip(data.item)">VOID</b-badge>
                  <b-badge v-else-if="data.item.replaces_id" variant="warning" class="ml-1"
                    v-b-tooltip.hover="'Corrected bill, replaces invoice #' + data.item.replaces_id">CORRECTED</b-badge>
                  <b-badge v-if="photoStatusLabel(data.item)" :variant="data.item.meter_image_upload_status === 'failed' ? 'danger' : 'info'"
                    class="ml-1" v-b-tooltip.hover="data.item.meter_image_upload_error || ''">{{ photoStatusLabel(data.item) }}</b-badge>
                </template>
                <template slot="owner" slot-scope="data">
                  {{ data.item.lot.lot_owner.name }}
                  <i v-if="data.item.hasVilogiSync" v-b-tooltip.hover="'Linked to a Vilogi account'"
                    class="fas fa-link text-success ml-1" style="font-size: 0.75rem;"></i>
                  <i v-else v-b-tooltip.hover="'Not linked to a Vilogi account yet — sync will fail until this owner is matched'"
                    class="fas fa-unlink text-muted ml-1" style="font-size: 0.75rem;"></i>
                </template>
                <template slot="units_consumed" slot-scope="data">
                  {{ rowConsumption(data.item) | formatNumber }}
                </template>
                <template slot="net_bill_value" slot-scope="data">
                  {{ lotTotalDue(data.item) | currency }}
                  <div v-if="Number(data.item.tax_percentage) > 0" class="text-muted small mt-1">
                    {{ data.item.tax_label || 'Tax' }} ({{ data.item.tax_percentage }}%)
                  </div>
                </template>
                <template slot="survey_start_date" slot-scope="data">
                  {{ fmtDateTime(data.item.survey_start_date) }}
                </template>
                <template slot="survey_start_by" slot-scope="data">
                  {{ data.item.user ? data.item.user.email : '-' }}
                </template>
                <template slot="survey_end_date" slot-scope="data">
                  {{ fmtDateTime(data.item.survey_end_date) }}
                </template>
                <template slot="surveyor_end_by" slot-scope="data">
                  {{ data.item.surveyor_closed.email }}
                </template>
                <template slot="vilogi" slot-scope="data">
                  <b-badge :variant="vilogiStatusVariant(data.item)" class="px-2 py-1"
                    v-b-tooltip.hover="vilogiStatusTooltip(data.item)">
                    {{ vilogiStatusLabel(data.item) }}
                  </b-badge>
                </template>
                <template slot="toggle_details" slot-scope="data">
                  <b-link v-b-tooltip.hover="data.detailsShowing ? 'Hide details' : 'Show details'"
                    @click="data.toggleDetails()" class="text-primary">
                    <i :class="data.detailsShowing ? 'fas fa-minus-circle' : 'fas fa-plus-circle'"></i>
                  </b-link>
                </template>
                <template slot="actions" slot-scope="data">
                  <b-link v-b-tooltip.hover="'View photo'" @click='viewLotImage(data)' class="text-success"><i
                      class="far fa-image mr-2"></i></b-link>
                  <b-link v-b-tooltip.hover="'View bill'"
                    :href="apiUrl + 'lot-pdf?' + 'archived_session_id=' + data.item.id + '&sp=' + data.item.service_provider_id"
                    class="text-danger"><i class="far fa-file-pdf mr-2"></i></b-link>
                  <b-link v-b-tooltip.hover="'View notification of usage'"
                    :href="apiUrl + 'lot-pdf?' + 'archived_session_id=' + data.item.id + '&sp=' + data.item.service_provider_id + '&view=notify'"
                    class="text-info"><i class="fas fa-exclamation-circle mr-2"></i></b-link>
                  <b-link v-if="data.item.canCorrect" v-b-tooltip.hover="'Correct reading'"
                    @click="openCorrectModal(data.item)" class="text-warning"><i class="fas fa-pen mr-2"></i></b-link>
                  <span v-else-if="data.item.correctBlockedReason && !['voided', 'not_round_row'].includes(data.item.correctBlockedReason)"
                    v-b-tooltip.hover="correctBlockedMessage(data.item.correctBlockedReason)" class="text-muted"><i class="fas fa-pen mr-2"></i></span>
                  <b-link v-if="canRetryVilogi(data.item) && !data.item.is_voided" v-b-tooltip.hover="'Retry Vilogi sync'"
                    @click="retryVilogi(data.item)" class="text-warning"><i class="fas fa-redo mr-2"></i></b-link>
                  <b-link v-if="canReverseVilogi(data.item)" v-b-tooltip.hover="'Reverse Vilogi entry'"
                    @click="reverseVilogi(data.item)" class="text-danger"><i class="fas fa-undo mr-2"></i></b-link>
                </template>
                <template slot="row-details" slot-scope="row">
                  <div class="lot-detail-accordion p-3">
                    <div class="row">
                      <div class="col-md-6">
                        <h6 class="text-uppercase text-muted mb-2">Vilogi Journal Entry</h6>
                        <div v-if="latestVilogiEntry(row.item)">
                          <p class="mb-1"><strong>Status:</strong> {{ vilogiStatusLabel(row.item) }}</p>
                          <p class="mb-1" v-if="latestVilogiEntry(row.item).external_id">
                            <strong>External ID:</strong> {{ latestVilogiEntry(row.item).external_id }}
                          </p>
                          <p class="mb-1" v-if="latestVilogiEntry(row.item).error_message">
                            <strong>Error:</strong> <span class="text-danger">{{ latestVilogiEntry(row.item).error_message }}</span>
                          </p>
                          <p class="mb-1" v-if="latestVilogiEntry(row.item).retry_count">
                            <strong>Retry count:</strong> {{ latestVilogiEntry(row.item).retry_count }}
                          </p>
                          <p class="mb-1" v-if="latestVilogiEntry(row.item).synced_at">
                            <strong>Synced at:</strong> {{ fmtDateTime(latestVilogiEntry(row.item).synced_at) }}
                          </p>
                          <p class="mb-1" v-if="latestVilogiEntry(row.item).reversed_at">
                            <strong>Reversed at:</strong> {{ fmtDateTime(latestVilogiEntry(row.item).reversed_at) }}
                          </p>
                        </div>
                        <p v-else class="text-muted mb-0">No Vilogi journal entry for this bill yet.</p>
                      </div>
                      <div class="col-md-6">
                        <h6 class="text-uppercase text-muted mb-2">Mail Schedule</h6>
                        <div v-if="mailHistory(row.item)">
                          <p class="mb-1">
                            <strong>Status:</strong>
                            <span v-if="mailFailed(row.item)" class="text-danger">
                              <i class="fas fa-times-circle mr-1"></i>Failed to deliver
                              <b-link class="ml-1 text-danger" @click="showMailError(mailHistory(row.item).message)" title="View error details">
                                <i class="fas fa-info-circle"></i>
                              </b-link>
                            </span>
                            <span v-else-if="mailDelivered(row.item)" class="text-success">
                              <i class="fas fa-check-circle mr-1"></i>{{ mailStatusLabel(row.item) }}
                            </span>
                            <span v-else>{{ mailStatusLabel(row.item) }}</span>
                          </p>
                          <p class="mb-1" v-if="(mailFailed(row.item) || mailDelivered(row.item)) && mailHistory(row.item).mail_delievered_on">
                            <strong>{{ mailFailed(row.item) ? 'Attempted on:' : 'Delivered on:' }}</strong> {{ fmtDateTime(mailHistory(row.item).mail_delievered_on) }}
                          </p>
                        </div>
                        <p v-else class="text-muted mb-0">No mail ever scheduled for this lot.</p>
                        <div v-if="row.item.is_voided" class="mt-3">
                          <h6 class="text-uppercase text-muted mb-2">{{ row.item.void_type === 'correction' ? 'Replaced by correction' : 'Void' }}</h6>
                          <p class="mb-1" v-if="row.item.voided_at"><strong>Voided at:</strong> {{ fmtDateTime(row.item.voided_at) }}</p>
                          <p class="mb-1"><strong>Reason:</strong> {{ row.item.voided_reason || 'Administrative correction' }}</p>
                          <p class="mb-1" v-if="row.item.replaced_by_id"><strong>Replaced by invoice:</strong> #{{ row.item.replaced_by_id }}</p>
                          <p class="mb-1">
                            <strong>{{ row.item.void_type === 'correction' ? 'Corrected-invoice email' : 'Void email' }}:</strong> {{ voidMailLabel(mailHistory(row.item)) }}
                          </p>
                          <p class="mb-1" v-if="mailHistory(row.item) && mailHistory(row.item).void_mail_sent_on">
                            <strong>Sent on:</strong> {{ fmtDateTime(mailHistory(row.item).void_mail_sent_on) }}
                          </p>
                          <p class="mb-1" :class="mailHistory(row.item) && mailHistory(row.item).void_mail_status === 'failed' ? 'text-danger' : 'text-muted'" v-if="mailHistory(row.item) && mailHistory(row.item).void_mail_message">
                            {{ mailHistory(row.item).void_mail_message }}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </template>
              </b-table>
              <div class="card-footer border-0">
                <b-pagination align="right" @input="pageClick(currentPage)" :total-rows="pagination.total"
                  v-model="currentPage" :per-page="pagination.per_page">
                </b-pagination>
              </div>
            </div>
            <div class="table-responsive" v-if="pagination.total == 0">
              <h5 class="py-5 text-center">No records available</h5>
            </div>
          </div>
        </div>
      </div>
    </div>

    <correct-row-modal ref="correctModal" @corrected="onRowChanged" @failed="onRowChanged"></correct-row-modal>

    <b-modal ref="imgModal" size="xl" hide-footer :title="'Lot No: ' + currentLotNo" :dialog-class="'fullscreen-modal'"
      centered no-close-on-backdrop no-close-on-esc @hidden="hideLoupe">
      <div class="image-viewer-container">
        <div v-if="loadingImage" class="spinner-border text-primary loader-center" role="status">
          <span class="sr-only">Loading Image...</span>
        </div>
        <template v-else>
          <b-button variant="light" class="download-btn" @click="downloadImage" v-b-tooltip.hover
            title="Download Image">
            <i class="fas fa-download"></i>
          </b-button>
          <div class="loupe-wrapper fullscreen-image-wrapper" @mouseenter="showLoupe" @mouseleave="hideLoupe"
            @mousemove="updateLoupe">
            <img ref="meterImage" :src="imgUrl" alt="Meter Image" class="fullscreen-image" @load="handleMeterImageLoad" />
            <div v-if="loupeVisible" class="loupe-lens" :style="loupeLensStyle"></div>
          </div>
        </template>
      </div>
    </b-modal>

    <b-modal ref="mailErrorModal" title="Email delivery error" ok-only ok-title="Close">
      <p class="mb-0" style="white-space: normal; overflow-wrap: break-word;">{{ mailErrorMessage }}</p>
    </b-modal>

    <b-modal ref="commonConsumptionModal" title="Supply History" ok-only ok-title="Close" dialog-class="fullscreen-modal supply-history-modal" size="xl">
      <div v-if="commonConsumptionHistory.data && commonConsumptionHistory.data.length">
        <div class="table-responsive">
          <b-table striped hover :items="commonConsumptionHistory.data" :fields="commonConsumptionFields">
            <template slot="cycle_date" slot-scope="data">
              {{ fmtDate(data.item.cycle_date) }}
            </template>
            <template slot="main_consumption" slot-scope="data">
              {{ data.item.main_consumption | formatNumber }}
            </template>
            <template slot="private_consumption" slot-scope="data">
              {{ data.item.private_consumption | formatNumber }}
            </template>
            <template slot="common_ratio" slot-scope="data">
              {{ formatPercent(data.item.common_ratio) }}
            </template>
            <template slot="tanker_volume" slot-scope="data">
              {{ data.item.tanker_volume != null ? (data.item.tanker_volume | formatNumber) : '-' }}
            </template>
            <template slot="total_supply" slot-scope="data">
              {{ (Number(data.item.main_consumption || 0) + Number(data.item.tanker_volume || 0)) | formatNumber }}
            </template>
            <template slot="tanker_cost" slot-scope="data">
              {{ data.item.tanker_cost != null ? (data.item.tanker_cost | currency) : '-' }}
            </template>
          </b-table>
        </div>
        <b-pagination
          v-if="commonConsumptionHistory.last_page > 1"
          v-model="ccPage"
          :total-rows="commonConsumptionHistory.total"
          :per-page="commonConsumptionHistory.per_page"
          align="right"
          class="mt-2 mb-0 px-3"
          @input="ccPageChanged"
        ></b-pagination>
      </div>
      <p v-else class="mb-0 text-center py-4">No supply history available.</p>
    </b-modal>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import dayjs from 'dayjs';
import { getPreferences, unitLabel, billingUnitLabel } from '@/utils/preferences';
import { formatDate, formatDateTime } from '@/utils/dateFormat';
import userData from '@/pages/Common/userData';
import correctRowModal from '@/pages/Common/correctRowModal';
import { correctBlockedMessage } from '@/utils/rowCorrection';
import { consumption } from '@/utils/readings';
import { isMailFailed } from '@/utils/mailStatus';
import ApiServices from '@/services/api';
import { apiUrl } from '@/config'
import noImageUrl from '@/assets/img/theme/noImage.png'

export default {
  // Detail view for a single closed session round — reached via a `view` route
  // query (base64 JSON: site{id,name}, service_type{id,service_name}, uuid, created_at),
  // set by completedSession.vue's row link or openSession.vue's post-close "View Readings".
  components: { userData, correctRowModal },
  data() {
    return {
      siteDetails: null,
      mailErrorMessage: '',
      currentLotNo: '',
      loadingImage: false,
      apiUrl,
      pagination: {},
      currentPage: 1,
      ccPage: 1,
      imgUrl: '',
      imgLoaded: false,
      loupeVisible: false,
      loupeLensStyle: {},
      entryOverrides: {},
      baseLotFields: [
        {
          key: 'toggle_details',
          label: '',
          sortable: false,
          'class': 'text-center',
          thStyle: { width: '40px' }
        },
        {
          key: 'id',
          label: 'Ref',
          sortable: true,
          'class': 'text-center'
        },
        {
          key: 'lot.lot_no',
          label: 'Lot No',
          sortable: true,
          'class': 'text-center'
        },
        {
          key: 'lot.lot_owner.name',
          label: 'Owner Name',
          sortable: true,
          'class': 'text-center'
        },
        {
          key: "units_consumed",
          label: "Units Consumed",
          sortable: true,
          class: "text-center"
        },
        {
          key: 'net_bill_value',
          label: 'Total Due',
          sortable: true,
          class: 'text-center'
        },
        {
          key: 'survey_start_date',
          label: 'Surevey Date',
          sortable: true,
          'class': 'text-center'
        },
        {
          key: 'survey_start_by',
          label: 'Surveyed BY',
          sortable: true,
          'class': 'text-center'
        },
        {
          key: 'vilogi',
          label: 'Vilogi',
          sortable: false,
          'class': 'text-center'
        },
        {
          key: 'actions',
          label: 'Actions',
          sortable: false,
          'class': 'text-center'
        },
      ],
      vilogiActionEntryId: null,
    }
  },
  computed: {
    ...mapGetters('sessions', ['completedSessionLots', 'commonConsumptionHistory']),
    prefs() {
      return getPreferences()
    },
    commonConsumptionFields() {
      const wu = unitLabel(1, this.prefs)
      return [
        { key: 'cycle_date',          label: 'Closed Session Date',    sortable: true,  class: 'text-center' },
        { key: 'main_consumption',    label: `Main Supply (${wu})`,    sortable: true,  class: 'text-center' },
        { key: 'tanker_volume',       label: `Tanker Volume (${wu})`,  sortable: true,  class: 'text-center' },
        { key: 'total_supply',        label: `Total Supply (${wu})`,   sortable: false, class: 'text-center' },
        { key: 'private_consumption', label: 'Total Billable Units',   sortable: true,  class: 'text-center' },
        { key: 'common_ratio',        label: 'Common Area Usage',      sortable: true,  class: 'text-center' },
        { key: 'tanker_cost',         label: 'Tanker Cost',            sortable: true,  class: 'text-center' }
      ]
    },
    sessionSummary() {
      return this.completedSessionLots && this.completedSessionLots.summary && this.completedSessionLots.summary.length
        ? this.completedSessionLots.summary[0]
        : {};
    },
    allDetailRows() {
      return this.completedSessionLots && this.completedSessionLots.data && this.completedSessionLots.data.data
        ? this.completedSessionLots.data.data
        : [];
    },
    mainMeterRow() {
      return this.allDetailRows.find(item => item.is_main === true) || null;
    },
    detailHeaderRow() {
      return this.detailRows[0] || this.mainMeterRow;
    },
    detailRows() {
      // Void rows get a greyed-out row variant. 2026-10-19
      return this.allDetailRows
        .filter(item => item.is_main !== true)
        .map(item => {
          if (item.is_voided) return Object.assign({}, item, { _rowVariant: 'secondary' })
          // Failed email deliveries get a red row so they don't read as sent. 2026-10-26
          if (this.mailFailed(item)) return Object.assign({}, item, { _rowVariant: 'danger' })
          return item
        });
    },
    // True when the closed round was billed at building level (both figures present; 0 is a real value, null is not). 2026-10-25
    hasUtilityBill() {
      const s = this.sessionSummary
      return s.utility_bill_total_units !== null && s.utility_bill_total_units !== undefined &&
        s.average_rate !== null && s.average_rate !== undefined
    },
    billingUnit() {
      const typeId = this.siteDetails && this.siteDetails.service_type ? this.siteDetails.service_type.id : 0
      return typeId ? billingUnitLabel(typeId, this.prefs) : 'units'
    },
    isWaterDetailSession() {
      return this.siteDetails && this.siteDetails.service_type && this.siteDetails.service_type.id === 1;
    },
    detailUnitLabel() {
      const typeId = this.siteDetails && this.siteDetails.service_type ? this.siteDetails.service_type.id : 0
      return typeId ? unitLabel(typeId, this.prefs) : 'units'
    },
    lotFields() {
      return this.baseLotFields.map(f => {
        if (f && f.key === 'units_consumed') {
          return { ...f, label: `Units Consumed (${this.detailUnitLabel})` }
        }
        return f
      })
    },
    allocationBasisLabel() {
      const allocationBasis = this.sessionSummary.allocation_basis || (this.detailRows[0] && this.detailRows[0].allocation_basis);
      if (allocationBasis === 'actual_consumption') {
        return 'Actual consumption';
      }
      if (allocationBasis === 'quote_part') {
        return 'Quote-part / millieme';
      }
      return '-';
    },
    allocationBasisMessage() {
      const allocationBasis = this.sessionSummary.allocation_basis || (this.detailRows[0] && this.detailRows[0].allocation_basis);
      if (allocationBasis === 'actual_consumption') {
        return 'Bowser cost allocated using actual private consumption with estimated historical common-area ratio.';
      }
      if (allocationBasis === 'quote_part') {
        return 'Bowser cost allocated using quote-part / millieme.';
      }
      return 'No bowser allocation applied to this session.';
    }
  },
  methods: {
    ...mapActions('sessions', ['COMPLETED_SESSION_LOTS', 'COMMON_CONSUMPTION_HISTORY']),
    fmtDate(d) {
      return formatDate(d)
    },
    fmtDateTime(d) {
      return formatDateTime(d)
    },
    copyUuid(uuid) {
      if (!navigator.clipboard) {
        this.ERROR_TOAST_MSG('Clipboard API not supported');
        return;
      }
      navigator.clipboard.writeText(uuid)
        .then(() => {
          this.SUCCESS_TOAST_MSG('UUID copied to clipboard!');
        })
        .catch(() => {
          this.ERROR_TOAST_MSG('Failed to copy UUID.');
        });
    },
    downloadImage() {
      const link = document.createElement('a');
      link.href = this.imgUrl;
      link.download = 'meter_image.jpg';
      link.click();
    },
    handleMeterImageLoad() {
      this.imgLoaded = true;
      this.hideLoupe();
    },
    showLoupe() {
      if (!this.imgLoaded) return;
      this.loupeVisible = true;
    },
    hideLoupe() {
      this.loupeVisible = false;
    },
    updateLoupe(event) {
      const image = event.currentTarget.querySelector('img');
      if (!image || !this.imgLoaded) return;

      const rect = image.getBoundingClientRect();
      const offsetX = event.clientX - rect.left;
      const offsetY = event.clientY - rect.top;

      if (offsetX < 0 || offsetY < 0 || offsetX > rect.width || offsetY > rect.height) {
        this.hideLoupe();
        return;
      }

      const lensSize = 180;
      const zoom = 2.5;
      const naturalWidth = image.naturalWidth || rect.width;
      const naturalHeight = image.naturalHeight || rect.height;
      const backgroundX = (offsetX / rect.width) * naturalWidth * zoom;
      const backgroundY = (offsetY / rect.height) * naturalHeight * zoom;

      this.loupeVisible = true;
      this.loupeLensStyle = {
        width: `${lensSize}px`,
        height: `${lensSize}px`,
        left: `${offsetX - lensSize / 2}px`,
        top: `${offsetY - lensSize / 2}px`,
        backgroundImage: `url("${image.currentSrc || image.src}")`,
        backgroundRepeat: 'no-repeat',
        backgroundSize: `${naturalWidth * zoom}px ${naturalHeight * zoom}px`,
        backgroundPosition: `-${backgroundX - lensSize / 2}px -${backgroundY - lensSize / 2}px`
      };
    },
    rowConsumption(item) {
      if (!item) return 0;
      if (item.units_consumed !== undefined && item.units_consumed !== null) {
        return Number(item.units_consumed);
      }
      return consumption(item.current_unit, item.previous_unit);
    },
    totalDue(item) {
      return Number(item.net_bill_value || 0) + Number(item.bowser_amount || 0);
    },
    lotTotalDue(item) {
      return Number(item.net_bill_value || 0) + Number(item.tax_value || 0);
    },
    formatPercent(value) {
      if (value === null || value === undefined || value === '') {
        return '-';
      }
      return `${(Number(value) * 100).toFixed(2)}%`;
    },
    showCommonConsumptionModal() {
      this.$refs.commonConsumptionModal.show();
    },
    ccPageChanged(page) {
      const data = this.siteDetails
      this.COMMON_CONSUMPTION_HISTORY({
        site_id: data.site.id,
        service_type_id: data.service_type.id,
        page
      }).catch(() => { });
    },
    loadLots(pageNo = 1) {
      const data = this.siteDetails
      const payload = {
        site_id: data.site.id,
        uuid: data.uuid,
        service_type_id: data.service_type.id,
        created_at: dayjs(data.created_at).format('YYYY-MM-DD'),
        page_no: pageNo
      }
      return this.COMPLETED_SESSION_LOTS(payload).then(() => {
        this.pagination = this.completedSessionLots.data;
        this.currentPage = pageNo
        if (data.service_type.id === 1) {
          this.ccPage = 1
          this.COMMON_CONSUMPTION_HISTORY({
            site_id: data.site.id,
            service_type_id: data.service_type.id,
            page: 1
          }).catch(() => { });
        }
      }).catch(() => { });
    },
    pageClick(pageNo) {
      this.loadLots(pageNo)
    },
    viewLotImage(data) {
      this.$refs.imgModal.show();
      this.imgUrl = noImageUrl;
      this.loadingImage = true;
      this.imgLoaded = false;
      this.hideLoupe();

      const path = data.item.meter_image_path || data.item.meter_image;
      this.currentLotNo = data.item.lot.lot_no || '';
      if (path) {
        const encodedPath = btoa(path);
        fetch(`${this.apiUrl}signed-url/${encodedPath}`, {
          headers: {
            Authorization: `Bearer ${localStorage.token}`,
          },
        })
          .then((res) => res.json())
          .then((res) => {
            if (res.status && res.url) {
              this.imgUrl = res.url;
            } else {
              this.imgUrl = noImageUrl;
            }
          })
          .catch(() => {
            this.imgUrl = noImageUrl;
          })
          .finally(() => {
            this.loadingImage = false;
          });
      } else {
        this.loadingImage = false;
      }
    },
    latestVilogiEntry(item) {
      const override = this.entryOverrides[item.id]
      const entries = (override ? override.accounting_journal_entries : item.accounting_journal_entries) || []
      return entries.length ? entries[0] : null
    },
    mailHistory(item) {
      const override = this.entryOverrides[item.id]
      return override ? override.mail_scheduled_history : (item.mail_scheduled_history || null)
    },
    refreshLotDetail(archivedSessionId) {
      return ApiServices.getArchivedSessionDetails(archivedSessionId)
        .then(response => {
          const detail = (response && response.data) || {}
          this.$set(this.entryOverrides, archivedSessionId, {
            accounting_journal_entries: detail.accounting_journal_entries || [],
            mail_scheduled_history: detail.mail_scheduled_history || null
          })
        })
        .catch(() => {})
    },
    vilogiStatusLabel(item) {
      const entry = this.latestVilogiEntry(item)
      if (!entry) return 'Not synced'
      if (entry.is_cancelled) return 'Cancelled'
      if (entry.status === 'synced') return 'Synced'
      if (entry.status === 'failed') return 'Failed'
      if (entry.status === 'reversed') return 'Reversed'
      if (entry.status === 'pending') return 'Pending'
      return entry.status
    },
    vilogiStatusVariant(item) {
      const entry = this.latestVilogiEntry(item)
      if (!entry) return 'light'
      if (entry.is_cancelled) return 'secondary'
      if (entry.status === 'synced') return 'success'
      if (entry.status === 'failed') return 'danger'
      if (entry.status === 'reversed') return 'dark'
      if (entry.status === 'pending') return 'warning'
      return 'light'
    },
    vilogiStatusTooltip(item) {
      const entry = this.latestVilogiEntry(item)
      if (!entry) return 'No Vilogi journal entry for this bill yet'
      if (entry.is_cancelled) return 'Cancelled before it was sent to Vilogi'
      if (entry.status === 'synced') return `Posted to Vilogi (external id: ${entry.external_id || '—'})`
      if (entry.status === 'failed') return entry.error_message || 'Sync failed'
      return `Status: ${entry.status}`
    },
    // Tooltip text describing when/why a lot's session was voided. 2026-10-19
    voidTooltip(item) {
      const when = item.voided_at ? this.fmtDateTime(item.voided_at) : ''
      const verb = item.void_type === 'correction' ? 'Replaced by a corrected bill' : 'Rolled back'
      return verb + (when ? ' on ' + when : '') + (item.voided_reason ? ': ' + item.voided_reason : '')
    },
    correctBlockedMessage,
    // Opens the correct-reading dialog for a lot row. 2026-10-19
    openCorrectModal(item) {
      this.$refs.correctModal.show(item)
    },
    // Refetches the lots after a correction attempt (success or failure), since canCorrect is only a snapshot. 2026-10-19
    onRowChanged() {
      this.loadLots(this.currentPage)
    },
    // Badge text for the async evidence-photo upload on a replacement row, or '' when nothing to show. 2026-10-19
    photoStatusLabel(item) {
      if (item.meter_image_upload_status === 'pending') return 'Photo uploading'
      if (item.meter_image_upload_status === 'failed') return 'Photo upload failed'
      return ''
    },
    // True when the lot's mail was processed but delivery failed (status 2 with a non-success message). 2026-10-26
    mailFailed(item) {
      const history = this.mailHistory(item)
      return !!history && Number(history.mail_scheduled_status) === 2 && isMailFailed(history)
    },
    // True when the lot's mail was processed (status 2) and delivered without a failure message. 2026-10-26
    mailDelivered(item) {
      const history = this.mailHistory(item)
      return !!history && Number(history.mail_scheduled_status) === 2 && !isMailFailed(history)
    },
    // Open the modal showing the full email delivery error. 2026-10-26
    showMailError(message) {
      this.mailErrorMessage = message
      this.$refs.mailErrorModal.show()
    },
    // Human label for a lot's mail schedule status (0 pending/not scheduled, 1 scheduled, 2 sent). 2026-10-19
    mailStatusLabel(item) {
      const history = this.mailHistory(item)
      const status = history ? Number(history.mail_scheduled_status) : null
      if (status === 1) return 'Scheduled (not yet sent)'
      if (status === 2) return 'Sent'
      return item.replaces_id ? 'Corrected invoice email pending' : 'Not scheduled'
    },
    // Human label for void_mail_status (sent / failed / skipped / null). 2026-10-19
    voidMailLabel(history) {
      const status = history && history.void_mail_status
      if (status === 'sent') return 'Sent'
      if (status === 'failed') return 'Failed'
      if (status === 'skipped') return 'Skipped (zero-value bill or no valid email)'
      return 'Not attempted'
    },
    canRetryVilogi(item) {
      const entry = this.latestVilogiEntry(item)
      return !!entry && entry.status === 'failed' && !entry.is_cancelled && (entry.retry_count || 0) < 3
    },
    canReverseVilogi(item) {
      const entry = this.latestVilogiEntry(item)
      return !!entry && entry.status === 'synced' && !entry.is_cancelled
    },
    retryVilogi(item) {
      const entry = this.latestVilogiEntry(item)
      if (!entry || this.vilogiActionEntryId) return
      if (!window.confirm('Retry posting this bill to Vilogi?')) return
      this.vilogiActionEntryId = entry.id
      ApiServices.retryVilogiEntry(entry.id)
        .then(response => {
          this.SUCCESS_TOAST_MSG(response.message || 'Entry queued for retry')
          return this.refreshLotDetail(item.id)
        })
        .catch(() => {})
        .finally(() => {
          this.vilogiActionEntryId = null
        })
    },
    reverseVilogi(item) {
      const entry = this.latestVilogiEntry(item)
      if (!entry || this.vilogiActionEntryId) return
      if (!window.confirm('Reverse this entry in Vilogi? This posts a real reversal and cannot be undone.')) return
      this.vilogiActionEntryId = entry.id
      ApiServices.reverseVilogiEntry(entry.id)
        .then(response => {
          this.SUCCESS_TOAST_MSG(response.message || 'Journal entry reversed successfully')
          return this.refreshLotDetail(item.id)
        })
        .catch(() => {})
        .finally(() => {
          this.vilogiActionEntryId = null
        })
    },
  },
  created() {
    document.body.classList.remove('modal-open');

    if (!localStorage.token) {
      localStorage.clear()
      this.$router.replace({ name: 'adminLogin' })
      return
    }
    if (localStorage.roleId == 1) {
      this.$router.replace({ name: "dashboard" });
      return
    }

    const routePayload = this.$route.query.view
    if (!routePayload) {
      this.$router.replace({ name: 'completedSession' })
      return
    }
    try {
      this.siteDetails = JSON.parse(atob(routePayload))
      this.loadLots(1)
    } catch (e) {
      this.$router.replace({ name: 'completedSession' })
    }
  },
  beforeDestroy() {
    document.body.classList.remove('modal-open');
  }
}
</script>

<style scoped>
.lot-detail-accordion {
  background: #f8f9fc;
  border-radius: 0.5rem;
  /* Table cells inherit nowrap; reset so long errors wrap inside the panel. 2026-10-20 */
  white-space: normal;
  overflow-wrap: break-word;
  word-break: break-word;
  text-align: left;
}

.session-summary-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 0;
  border-radius: 10px;
  background: #f8fafd;
  border: 1px solid #dbeafe;
}

.stat-chip {
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 12px 20px;
  border-right: 1px solid #e2eaf5;
  min-width: 140px;
}

.stat-chip:last-child {
  border-right: none;
}

.stat-chip--action {
  justify-content: center;
  min-width: 160px;
}

.stat-label {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #94a3b8;
  margin-bottom: 3px;
  white-space: nowrap;
}

.stat-value {
  font-size: 14px;
  font-weight: 700;
  color: #1e293b;
  line-height: 1.3;
}

.stat-note {
  font-size: 11px;
  color: #94a3b8;
  margin-top: 2px;
  line-height: 1.3;
}

.loupe-wrapper {
  position: relative;
  display: inline-block;
  cursor: zoom-in;
  overflow: hidden;
}

.image-viewer-container {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #000;
}

.loader-center {
  margin: 0 auto;
}

.fullscreen-modal {
  max-width: 100vw !important;
  height: 100vh !important;
  margin: 0 !important;
}

.fullscreen-modal .modal-content {
  height: 100vh;
}

.fullscreen-modal .modal-body {
  padding: 0;
  display: flex;
}

.fullscreen-image-wrapper {
  max-width: 100%;
  max-height: 100%;
}

.fullscreen-image {
  display: block;
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.download-btn {
  position: absolute;
  top: 20px;
  right: 20px;
  z-index: 1000;
}

.loupe-lens {
  position: absolute;
  border: 2px solid rgba(255, 255, 255, 0.95);
  border-radius: 50%;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
  pointer-events: none;
}

::v-deep .supply-history-modal .modal-body {
  padding: 1rem;
  overflow-y: auto;
  flex: 1;
  display: block;
}
</style>



// WEBPACK FOOTER //
// src/pages/Admin/completedSessionView.vue